<template>
  <div class="comprobantes-admin">
    <div class="cabecera">
      <h3 class="titulo"><i class="bi bi-file-earmark-check me-2"></i>Comprobantes de pago</h3>
      <div class="filtros">
        <select v-model="filtroEstado" class="filtro-select" @change="cargar">
          <option value="">Todos</option>
          <option value="pendiente">Pendientes</option>
          <option value="aprobado">Aprobados</option>
          <option value="rechazado">Rechazados</option>
        </select>
        <button class="btn-refrescar" @click="cargar" :disabled="cargando" aria-label="Recargar lista">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
      </div>
    </div>

    <div v-if="cargando" class="estado-carga" role="status" aria-live="polite">
      <span class="spinner" aria-hidden="true"></span>Cargando...
    </div>

    <div v-else-if="filas.length === 0" class="sin-datos">
      No hay comprobantes con el filtro seleccionado.
    </div>

    <div v-else class="tabla-contenedor">
      <table class="tabla" role="table">
        <thead>
          <tr>
            <th scope="col">Empresa</th>
            <th scope="col">Banco</th>
            <th scope="col">N. Comprobante</th>
            <th scope="col">Fecha transf.</th>
            <th scope="col">Archivo</th>
            <th scope="col">Nota</th>
            <th scope="col">Recibido</th>
            <th scope="col">Gracia expira</th>
            <th scope="col">Estado</th>
            <th scope="col">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in filas" :key="c.id" :class="{ 'fila-urgente': esUrgente(c) }">
            <td>
              <div class="empresa-nombre">{{ c.razon_social || '-' }}</div>
              <div class="empresa-ruc">{{ c.ruc }}</div>
            </td>
            <td>{{ c.banco }}</td>
            <td class="mono">{{ c.numero_comprobante }}</td>
            <td>{{ c.fecha_transferencia }}</td>
            <td>
              <button class="btn-ver" @click="abrirVisor(c)" :title="c.archivo_nombre">
                <i :class="iconoMime(c.archivo_mime)"></i> Ver
              </button>
            </td>
            <td class="nota-cell">{{ c.nota || '-' }}</td>
            <td class="fecha-cell">{{ formatFecha(c.creado_en) }}</td>
            <td :class="{ 'celda-urgente': esUrgente(c) }">{{ formatFecha(c.gracia_expira) }}</td>
            <td>
              <span :class="['badge-estado', `estado-${c.estado}`]">{{ c.estado }}</span>
              <div v-if="c.motivo_rechazo" class="motivo">{{ c.motivo_rechazo }}</div>
            </td>
            <td>
              <template v-if="c.estado === 'pendiente'">
                <button class="btn-aprobar" @click="abrirAprobacion(c)" :disabled="procesando === c.id">
                  <i class="bi bi-check-lg"></i> Aprobar
                </button>
                <button class="btn-rechazar" @click="abrirRechazo(c)" :disabled="procesando === c.id">
                  <i class="bi bi-x-lg"></i> Rechazar
                </button>
              </template>
              <span v-else class="revisado-info">
                Revisado {{ formatFecha(c.revisado_en) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal confirmacion aprobacion -->
    <div v-if="modalAprobacion" class="modal-backdrop" @click.self="modalAprobacion = null" role="dialog" aria-modal="true" aria-labelledby="titulo-aprobacion">
      <div class="modal-caja">
        <h5 id="titulo-aprobacion">Aprobar comprobante</h5>
        <p class="modal-empresa">
          ¿Confirmar aprobacion del comprobante de <strong>{{ modalAprobacion.razon_social }}</strong>?
          <br>
          <span class="modal-detalle">{{ modalAprobacion.banco }} / {{ modalAprobacion.numero_comprobante }}</span>
        </p>
        <p class="modal-aviso">La suscripcion de la empresa sera extendida 30 dias.</p>
        <div class="modal-acciones">
          <button class="btn-aprobar" @click="confirmarAprobacion" :disabled="procesando === modalAprobacion.id">
            <i class="bi bi-check-lg"></i> Confirmar aprobacion
          </button>
          <button class="btn-cancelar" @click="modalAprobacion = null">Cancelar</button>
        </div>
      </div>
    </div>

    <!-- Panel lateral de previsualización -->
    <transition name="panel-slide">
      <div v-if="visor" class="visor-overlay" @click.self="visor = null" role="dialog" aria-modal="true" aria-labelledby="titulo-visor">
        <div class="visor-panel">
          <div class="visor-cabecera">
            <span id="titulo-visor" class="visor-titulo">Comprobante de pago</span>
            <button class="visor-cerrar" @click="visor = null" aria-label="Cerrar visor">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="visor-datos">
            <div class="visor-dato">
              <span class="visor-etiqueta">Empresa</span>
              <span class="visor-valor">{{ visor.razon_social || '-' }}</span>
            </div>
            <div class="visor-dato">
              <span class="visor-etiqueta">RUC</span>
              <span class="visor-valor mono">{{ visor.ruc || '-' }}</span>
            </div>
            <div class="visor-dato">
              <span class="visor-etiqueta">Banco / Cooperativa</span>
              <span class="visor-valor">{{ visor.banco }}</span>
            </div>
            <div class="visor-dato">
              <span class="visor-etiqueta">N. Comprobante</span>
              <span class="visor-valor mono">{{ visor.numero_comprobante }}</span>
            </div>
            <div class="visor-dato">
              <span class="visor-etiqueta">Fecha transferencia</span>
              <span class="visor-valor">{{ visor.fecha_transferencia }}</span>
            </div>
            <div class="visor-dato">
              <span class="visor-etiqueta">Recibido</span>
              <span class="visor-valor">{{ formatFecha(visor.creado_en) }}</span>
            </div>
            <div class="visor-dato">
              <span class="visor-etiqueta">Gracia expira</span>
              <span class="visor-valor" :class="{ 'celda-urgente': esUrgente(visor) }">{{ formatFecha(visor.gracia_expira) }}</span>
            </div>
            <div class="visor-dato">
              <span class="visor-etiqueta">Estado</span>
              <span :class="['badge-estado', `estado-${visor.estado}`]">{{ visor.estado }}</span>
            </div>
            <div v-if="visor.nota" class="visor-dato">
              <span class="visor-etiqueta">Nota</span>
              <span class="visor-valor">{{ visor.nota }}</span>
            </div>
            <div v-if="visor.motivo_rechazo" class="visor-dato">
              <span class="visor-etiqueta">Motivo rechazo</span>
              <span class="visor-valor motivo">{{ visor.motivo_rechazo }}</span>
            </div>
          </div>

          <div class="visor-zoom-barra">
            <button class="btn-zoom" @click="nivelZoom = Math.max(0.5, nivelZoom - 0.25)" :disabled="nivelZoom <= 0.5" aria-label="Reducir zoom">
              <i class="bi bi-zoom-out"></i>
            </button>
            <span class="zoom-nivel">{{ Math.round(nivelZoom * 100) }}%</span>
            <button class="btn-zoom" @click="nivelZoom = Math.min(4, nivelZoom + 0.25)" :disabled="nivelZoom >= 4" aria-label="Aumentar zoom">
              <i class="bi bi-zoom-in"></i>
            </button>
            <button class="btn-zoom btn-zoom-reset" @click="nivelZoom = 1" :disabled="nivelZoom === 1" aria-label="Restablecer zoom">
              <i class="bi bi-aspect-ratio"></i>
            </button>
          </div>

          <div class="visor-archivo">
            <div class="visor-zoom-contenido" :style="{ width: nivelZoom !== 1 ? (nivelZoom * 100) + '%' : '100%' }">
              <iframe
                v-if="visor.archivo_mime === 'application/pdf'"
                :src="urlArchivo(visor.archivo_path)"
                class="visor-iframe"
                title="Comprobante PDF"
              ></iframe>
              <img
                v-else
                :src="urlArchivo(visor.archivo_path)"
                class="visor-imagen"
                alt="Imagen del comprobante"
              />
            </div>
          </div>

          <div v-if="visor.estado === 'pendiente'" class="visor-acciones">
            <button class="btn-aprobar" @click="abrirAprobacionDesdeVisor" :disabled="procesando === visor.id">
              <i class="bi bi-check-lg"></i> Aprobar
            </button>
            <button class="btn-rechazar" @click="abrirRechazoDesdeVisor" :disabled="procesando === visor.id">
              <i class="bi bi-x-lg"></i> Rechazar
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal rechazo -->
    <div v-if="modalRechazo" class="modal-backdrop" @click.self="modalRechazo = null" role="dialog" aria-modal="true" aria-labelledby="titulo-rechazo">
      <div class="modal-caja">
        <h5 id="titulo-rechazo">Rechazar comprobante</h5>
        <p class="modal-empresa">{{ modalRechazo.razon_social }} &mdash; {{ modalRechazo.banco }} / {{ modalRechazo.numero_comprobante }}</p>
        <label class="campo-label" for="motivo-rechazo">Motivo del rechazo (opcional)</label>
        <textarea id="motivo-rechazo" v-model="motivoRechazo" class="campo-textarea" rows="3" maxlength="500" placeholder="Indique la razon del rechazo..."></textarea>
        <div class="modal-acciones">
          <button class="btn-rechazar" @click="confirmarRechazo" :disabled="procesando === modalRechazo.id">
            Confirmar rechazo
          </button>
          <button class="btn-cancelar" @click="modalRechazo = null">Cancelar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import notificacion from '@/services/notificationService'

export default {
  name: 'ComprobantesAdmin',
  data() {
    return {
      filas: [],
      cargando: false,
      filtroEstado: 'pendiente',
      procesando: null,
      visor: null,
      nivelZoom: 1,
      modalAprobacion: null,
      modalRechazo: null,
      motivoRechazo: '',
    }
  },
  mounted() {
    this.cargar()
  },
  methods: {
    async cargar() {
      this.cargando = true
      try {
        const params = this.filtroEstado ? { estado: this.filtroEstado } : {}
        const { data } = await api.get('/admin/suscripcion/comprobantes', { params })
        this.filas = Array.isArray(data?.data) ? data.data : []
      } catch {
        notificacion.error('No se pudieron cargar los comprobantes.')
      } finally {
        this.cargando = false
      }
    },
    abrirVisor(c) {
      this.visor = c
      this.nivelZoom = 1
    },
    abrirAprobacionDesdeVisor() {
      this.modalAprobacion = this.visor
      this.visor = null
    },
    abrirRechazoDesdeVisor() {
      this.abrirRechazo(this.visor)
      this.visor = null
    },
    abrirAprobacion(c) {
      this.modalAprobacion = c
    },
    async confirmarAprobacion() {
      if (!this.modalAprobacion) return
      this.procesando = this.modalAprobacion.id
      try {
        await api.post(`/admin/suscripcion/comprobantes/${this.modalAprobacion.id}/aprobar`)
        notificacion.success('Comprobante aprobado. La suscripcion fue extendida 30 dias.')
        this.modalAprobacion = null
        await this.cargar()
      } catch (e) {
        notificacion.error(e?.response?.data?.error || 'Error al aprobar.')
      } finally {
        this.procesando = null
      }
    },
    abrirRechazo(c) {
      this.modalRechazo = c
      this.motivoRechazo = ''
    },
    async confirmarRechazo() {
      if (!this.modalRechazo) return
      this.procesando = this.modalRechazo.id
      try {
        await api.post(`/admin/suscripcion/comprobantes/${this.modalRechazo.id}/rechazar`, {
          motivo: this.motivoRechazo.trim()
        })
        notificacion.success('Comprobante rechazado.')
        this.modalRechazo = null
        await this.cargar()
      } catch (e) {
        notificacion.error(e?.response?.data?.error || 'Error al rechazar.')
      } finally {
        this.procesando = null
      }
    },
    urlArchivo(path) {
      const base = process.env.VUE_APP_API_BASE_URL?.replace('/backend_mvc/public', '') || 'http://localhost'
      return base + path
    },
    iconoMime(mime) {
      if (mime === 'application/pdf') return 'bi bi-file-earmark-pdf texto-peligro'
      return 'bi bi-file-earmark-image texto-primario'
    },
    esUrgente(c) {
      if (c.estado !== 'pendiente' || !c.gracia_expira) return false
      return new Date(c.gracia_expira) < new Date()
    },
    formatFecha(dt) {
      if (!dt) return '-'
      return dt.replace('T', ' ').substring(0, 16)
    },
  }
}
</script>

<style scoped>
/* ─── Layout ─────────────────────────────────────────────────────── */
.comprobantes-admin {
  padding: 24px;
  color: var(--text-primary);
}

.cabecera {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
}

.titulo {
  font-size: 1.35rem;
  font-weight: 600;
  margin: 0;
  color: var(--text-primary);
}

.filtros {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* ─── Filtro select ──────────────────────────────────────────────── */
.filtro-select {
  padding: 4px 8px;
  border: 1px solid var(--border-main);
  border-radius: 6px;
  font-size: 0.9rem;
  background: var(--secondary-bg-main);
  color: var(--text-primary);
  outline: none;
  transition: border-color 0.15s;
}

.filtro-select:focus {
  border-color: var(--border-focus);
}

/* ─── Boton refrescar ────────────────────────────────────────────── */
.btn-refrescar {
  background: var(--bg-main);
  border: 1px solid var(--border-main);
  border-radius: 6px;
  padding: 5px 10px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: background 0.15s, border-color 0.15s;
}

.btn-refrescar:hover:not(:disabled) {
  background: var(--border-main);
}

.btn-refrescar:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ─── Estado carga / sin datos ───────────────────────────────────── */
.estado-carga,
.sin-datos {
  padding: 32px;
  text-align: center;
  color: var(--text-muted);
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid var(--border-main);
  border-top-color: var(--primary-main);
  border-radius: 50%;
  animation: girar 0.6s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
}

@keyframes girar {
  to { transform: rotate(360deg); }
}

/* ─── Tabla ──────────────────────────────────────────────────────── */
.tabla-contenedor {
  overflow-x: auto;
}

.tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}

.tabla th,
.tabla td {
  padding: 9px 10px;
  border-bottom: 1px solid var(--border-main);
  text-align: left;
  vertical-align: top;
}

.tabla th {
  background: var(--bg-main);
  color: var(--text-secondary);
  font-weight: 600;
  white-space: nowrap;
}

.fila-urgente {
  background: color-mix(in srgb, var(--accent-warning) 12%, transparent);
}

/* ─── Celdas ─────────────────────────────────────────────────────── */
.empresa-nombre {
  font-weight: 600;
  color: var(--text-primary);
}

.empresa-ruc {
  font-size: 0.8rem;
  color: var(--text-muted);
}

.mono {
  font-family: monospace;
  color: var(--text-primary);
}

.nota-cell,
.fecha-cell {
  max-width: 160px;
  word-break: break-word;
  color: var(--text-secondary);
}

.celda-urgente {
  color: var(--accent-danger);
  font-weight: 600;
}

.link-archivo {
  color: var(--primary-main);
  text-decoration: none;
  white-space: nowrap;
}

.link-archivo:hover {
  color: var(--primary-hover);
  text-decoration: underline;
}

.texto-peligro  { color: var(--accent-danger); }
.texto-primario { color: var(--primary-main); }

.motivo {
  font-size: 0.8rem;
  color: var(--accent-danger);
  margin-top: 2px;
}

.revisado-info {
  font-size: 0.8rem;
  color: var(--text-muted);
}

/* ─── Badges de estado ───────────────────────────────────────────── */
.badge-estado {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
}

.estado-pendiente {
  background: color-mix(in srgb, var(--accent-warning) 18%, transparent);
  color: var(--accent-warning);
  border: 1px solid color-mix(in srgb, var(--accent-warning) 35%, transparent);
}

.estado-aprobado {
  background: color-mix(in srgb, var(--accent-success) 18%, transparent);
  color: var(--accent-success);
  border: 1px solid color-mix(in srgb, var(--accent-success) 35%, transparent);
}

.estado-rechazado {
  background: color-mix(in srgb, var(--accent-danger) 18%, transparent);
  color: var(--accent-danger);
  border: 1px solid color-mix(in srgb, var(--accent-danger) 35%, transparent);
}

/* ─── Botones de accion ──────────────────────────────────────────── */
.btn-aprobar,
.btn-rechazar,
.btn-cancelar {
  padding: 4px 10px;
  border: none;
  border-radius: 5px;
  font-size: 0.82rem;
  cursor: pointer;
  margin: 2px 2px 0 0;
  font-weight: 500;
  transition: opacity 0.15s;
}

.btn-aprobar  { background: var(--accent-success); color: #fff; }
.btn-rechazar { background: var(--accent-danger);  color: #fff; }
.btn-cancelar { background: var(--text-muted);     color: #fff; }

.btn-aprobar:hover:not(:disabled)  { opacity: 0.85; }
.btn-rechazar:hover:not(:disabled) { opacity: 0.85; }
.btn-cancelar:hover:not(:disabled) { opacity: 0.85; }

.btn-aprobar:disabled,
.btn-rechazar:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ─── Modal ──────────────────────────────────────────────────────── */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-caja {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  padding: 24px;
  max-width: 480px;
  width: 100%;
  box-shadow: var(--shadow-md);
  color: var(--text-primary);
}

.modal-caja h5 {
  margin: 0 0 8px;
  font-size: 1.1rem;
  color: var(--text-primary);
}

.modal-empresa {
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-bottom: 12px;
}

.modal-detalle {
  font-size: 0.85rem;
  color: var(--text-muted);
}

.modal-aviso {
  font-size: 0.88rem;
  color: var(--accent-success);
  margin-bottom: 4px;
}

.campo-label {
  font-size: 0.9rem;
  font-weight: 600;
  display: block;
  margin-bottom: 4px;
  color: var(--text-primary);
}

.campo-textarea {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--border-main);
  border-radius: 6px;
  font-size: 0.9rem;
  background: var(--bg-main);
  color: var(--text-primary);
  resize: vertical;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.campo-textarea:focus {
  border-color: var(--border-focus);
}

.modal-acciones {
  display: flex;
  gap: 8px;
  margin-top: 14px;
}

/* ─── Boton Ver ──────────────────────────────────────────────────── */
.btn-ver {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border: 1px solid var(--border-main);
  border-radius: 5px;
  font-size: 0.82rem;
  background: var(--secondary-bg-main);
  color: var(--primary-main);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  white-space: nowrap;
}

.btn-ver:hover {
  background: color-mix(in srgb, var(--primary-main) 10%, transparent);
  border-color: var(--primary-main);
}

/* ─── Panel lateral visor ────────────────────────────────────────── */
.visor-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1100;
  display: flex;
  justify-content: flex-end;
}

.visor-panel {
  width: 600px;
  max-width: 96vw;
  height: 100%;
  background: var(--secondary-bg-main);
  border-left: 1px solid var(--border-main);
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.visor-cabecera {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid var(--border-main);
  flex-shrink: 0;
}

.visor-titulo {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.visor-cerrar {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  font-size: 1rem;
  padding: 4px 6px;
  border-radius: 4px;
  transition: background 0.15s, color 0.15s;
}

.visor-cerrar:hover {
  background: color-mix(in srgb, var(--accent-danger) 12%, transparent);
  color: var(--accent-danger);
}

.visor-datos {
  padding: 14px 18px;
  border-bottom: 1px solid var(--border-main);
  display: flex;
  flex-direction: column;
  gap: 7px;
  flex-shrink: 0;
}

.visor-dato {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  font-size: 0.85rem;
}

.visor-etiqueta {
  color: var(--text-muted);
  flex-shrink: 0;
  min-width: 130px;
}

.visor-valor {
  color: var(--text-primary);
  text-align: right;
  word-break: break-all;
}

.visor-zoom-barra {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-bottom: 1px solid var(--border-main);
  background: var(--bg-main);
  flex-shrink: 0;
}

.btn-zoom {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 5px;
  padding: 3px 8px;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: 0.88rem;
  transition: background 0.15s, border-color 0.15s;
  line-height: 1.4;
}

.btn-zoom:hover:not(:disabled) {
  background: color-mix(in srgb, var(--primary-main) 10%, transparent);
  border-color: var(--primary-main);
  color: var(--primary-main);
}

.btn-zoom:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-zoom-reset {
  margin-left: 4px;
}

.zoom-nivel {
  font-size: 0.82rem;
  color: var(--text-muted);
  min-width: 40px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.visor-archivo {
  flex: 1;
  overflow: auto;
  padding: 16px;
  background: var(--bg-main);
}

.visor-zoom-contenido {
  min-width: 100%;
  transition: width 0.15s ease;
}

.visor-imagen {
  width: 100%;
  height: auto;
  border-radius: 6px;
  box-shadow: var(--shadow-sm);
  display: block;
  max-width: none;
}

.visor-iframe {
  width: 100%;
  height: 100%;
  min-height: 420px;
  border: none;
  border-radius: 4px;
}

.visor-acciones {
  padding: 12px 18px;
  border-top: 1px solid var(--border-main);
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

/* ─── Animacion panel ────────────────────────────────────────────── */
.panel-slide-enter-active,
.panel-slide-leave-active {
  transition: opacity 0.22s ease;
}

.panel-slide-enter-active .visor-panel,
.panel-slide-leave-active .visor-panel {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.panel-slide-enter-from,
.panel-slide-leave-to {
  opacity: 0;
}

.panel-slide-enter-from .visor-panel,
.panel-slide-leave-to .visor-panel {
  transform: translateX(100%);
}
</style>
